import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Clock, Search, Smartphone, MessageSquare, BarChart2, Activity, Info } from 'lucide-react';
import { cn } from '../../utils/cn';
import api from '../../services/api';

const MetaCampaignDetailsModal = ({ campaignId, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchDetails();
    const interval = setInterval(fetchDetails, 10000); // Auto-refresh while open
    return () => clearInterval(interval);
  }, [campaignId]);

  const fetchDetails = async () => {
    try {
      const res = await api.get(`/meta/campaigns/${campaignId}`);
      setDetails(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
      case 'read':
        return <CheckCircle className="w-4 h-4 text-[#2E844A]" />;
      case 'sent':
      case 'queued':
        return <Clock className="w-4 h-4 text-[#0176D3]" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-[#BA0517]" />;
      default:
        return <Activity className="w-4 h-4 text-[#706E6B]" />;
    }
  };

  const filteredContacts = details?.contacts.filter(c => {
    if (search && !c.phoneNumber?.includes(search.trim())) return false;
    if (filter === 'all') return true;
    if (filter === 'failed') return c.status === 'failed';
    if (filter === 'success') return ['sent', 'delivered', 'read'].includes(c.status);
    return true;
  }) || [];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-end bg-black/40 p-0">
      <div className="bg-white w-full max-w-2xl h-full shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col border-l border-[#D8DDE6]">

        {/* Header */}
        <div className="h-20 px-6 border-b border-[#D8DDE6] flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-[#0176D3] flex items-center justify-center text-white">
              <BarChart2 size={18} />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-[#080707]">{details?.name || 'Campaign Details'}</h2>
              <p className="text-[11px] text-[#706E6B] flex items-center gap-2">
                <Clock size={10} /> {details ? new Date(details.createdAt).toLocaleString() : 'Loading…'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F4F6F9] rounded transition-all" aria-label="Close">
            <X className="w-5 h-5 text-[#706E6B]" />
          </button>
        </div>

        {loading ? (
           <div className="flex-1 flex flex-col items-center justify-center space-y-4">
             <div className="w-8 h-8 border-4 border-[#0176D3] border-t-transparent rounded-full animate-spin" />
             <p className="text-[12px] font-bold text-[#706E6B]">Loading campaign data…</p>
           </div>
        ) : (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 border-b border-[#D8DDE6] bg-[#F4F6F9] shrink-0">
               <div className="p-4 text-center border-r border-[#D8DDE6]">
                 <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Total</p>
                 <p className="text-[18px] font-bold text-[#080707]">{details.stats.total}</p>
               </div>
               <div className="p-4 text-center border-r border-[#D8DDE6]">
                 <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Sent</p>
                 <p className="text-[18px] font-bold text-[#0176D3]">{details.stats.sent}</p>
               </div>
               <div className="p-4 text-center border-r border-[#D8DDE6]">
                 <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Read</p>
                 <p className="text-[18px] font-bold text-[#2E844A]">{details.stats.read}</p>
               </div>
               <div className="p-4 text-center">
                 <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Failed</p>
                 <p className="text-[18px] font-bold text-[#BA0517]">{details.stats.failed}</p>
               </div>
            </div>

            {/* Contact List */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="px-6 py-4 bg-white border-b border-[#D8DDE6] flex items-center justify-between shrink-0">
                 <div className="flex gap-2">
                   {['all', 'success', 'failed'].map(f => (
                     <button
                       key={f}
                       onClick={() => setFilter(f)}
                       className={cn(
                         "px-3 py-1 rounded text-[11px] font-bold uppercase transition-all",
                         filter === f ? "bg-[#0176D3] text-white" : "bg-white text-[#706E6B] border border-[#D8DDE6] hover:border-[#0176D3]"
                       )}
                     >
                       {f}
                     </button>
                   ))}
                 </div>
                 <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#706E6B]" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search number…"
                      className="h-8 pl-9 pr-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[12px] outline-none focus:bg-white focus:border-[#0176D3] transition-all w-40"
                    />
                 </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-2">
                {filteredContacts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Smartphone className="w-10 h-10 text-[#D8DDE6] mb-3" />
                    <p className="text-[13px] font-bold text-[#706E6B]">No matching contacts</p>
                  </div>
                ) : (
                  filteredContacts.map((contact, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-white border border-[#D8DDE6] rounded hover:shadow-sm transition-all">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-9 h-9 rounded flex items-center justify-center transition-all",
                          contact.status === 'failed' ? "bg-[#FDE7E9]" : "bg-[#F4F6F9]"
                        )}>
                          <Smartphone size={16} className={contact.status === 'failed' ? "text-[#BA0517]" : "text-[#706E6B]"} />
                        </div>
                        <div>
                          <p className="text-[13px] font-bold text-[#080707]">{contact.phoneNumber}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                             {getStatusIcon(contact.status)}
                             <span className={cn(
                               "text-[10px] font-bold uppercase tracking-wider",
                               contact.status === 'failed' ? "text-[#BA0517]" : "text-[#706E6B]"
                             )}>{contact.status}</span>
                             {contact.processedAt && (
                               <span className="text-[10px] text-[#706E6B] ml-1">{new Date(contact.processedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                             )}
                          </div>
                        </div>
                      </div>

                      {contact.error && (
                        <div className="px-3 py-1 bg-[#FDE7E9] border border-[#F6A3AB] rounded max-w-[200px]">
                           <p className="text-[10px] text-[#BA0517] font-medium leading-tight truncate">{contact.error}</p>
                        </div>
                      )}

                      {!contact.error && contact.variables?.length > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-[#EAF4FE] rounded">
                           <Info size={12} className="text-[#0176D3]" />
                           <span className="text-[10px] font-bold text-[#0176D3] uppercase">{contact.variables.length} variables</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 bg-[#F4F6F9] border-t border-[#D8DDE6] space-y-3">
              <div className="flex items-center gap-2">
                 <div className="p-1.5 bg-white rounded border border-[#D8DDE6]"><MessageSquare size={13} className="text-[#0176D3]" /></div>
                 <h4 className="text-[11px] font-bold text-[#080707] uppercase tracking-wider">Template Details</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                 <div className="p-3 bg-white rounded border border-[#D8DDE6] space-y-1">
                    <p className="text-[10px] font-bold text-[#706E6B] uppercase">Template Name</p>
                    <p className="text-[13px] font-bold text-[#080707]">{details.templateId?.name || 'N/A'}</p>
                 </div>
                 <div className="p-3 bg-white rounded border border-[#D8DDE6] space-y-1">
                    <p className="text-[10px] font-bold text-[#706E6B] uppercase">Category</p>
                    <p className="text-[13px] font-bold text-[#0176D3]">{details.templateId?.category || 'N/A'}</p>
                 </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MetaCampaignDetailsModal;
